import React, { useState } from 'react';
import { Users, ExternalLink, X, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';

/**
 * CrossRoomWaitingBanner — pops up in a host's currently-active meeting
 * when one of their OTHER rooms has guests stuck in its waiting room.
 *
 * Two actions per stuck room:
 *   - "Admit all" — server-side force-admits everyone (ownership-checked)
 *   - "Open room" — opens the other meeting in a new tab so the host can
 *      see it for themselves and run the meeting there going forward
 *
 * Props:
 *   queues       — array of { meetingId, meetingName, waitingList }
 *   onAdmitAll   — async (meetingId) => { success, count, message? }
 *   onDismiss    — (meetingId) => void
 */
export default function CrossRoomWaitingBanner({ queues, onAdmitAll, onDismiss }) {
  const [admitting, setAdmitting] = useState({}); // { [meetingId]: true }

  if (!queues || queues.length === 0) return null;

  return (
    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[120] flex flex-col gap-2 max-w-xl w-[calc(100%-1.5rem)] pointer-events-none">
      {queues.map((queue) => {
        const count = queue.waitingList?.length || 0;
        const isAdmitting = !!admitting[queue.meetingId];
        return (
          <div
            key={queue.meetingId}
            className="pointer-events-auto bg-gradient-to-r from-[#6a1fbf]/95 to-[#00c853]/85 backdrop-blur border border-white/20 rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.4)] px-4 py-3 flex items-center gap-3 text-white"
          >
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/15 border border-white/30 flex items-center justify-center">
              <Users className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">
                {count} {count === 1 ? 'guest is' : 'guests are'} waiting in{' '}
                <span className="underline decoration-white/40 underline-offset-2">{queue.meetingName}</span>
              </div>
              <div className="text-[11px] text-white/80 truncate">
                {(queue.waitingList || []).slice(0, 4).map((p) => p.odName).join(', ')}
                {count > 4 ? ` and ${count - 4} more` : ''}
              </div>
            </div>
            <button
              onClick={async () => {
                setAdmitting((prev) => ({ ...prev, [queue.meetingId]: true }));
                try {
                  const resp = await onAdmitAll?.(queue.meetingId);
                  if (resp?.success) {
                    toast.success(`Admitted ${resp.count || count} guest${(resp.count || count) === 1 ? '' : 's'}`);
                  } else {
                    toast.error(resp?.message || 'Could not admit guests');
                  }
                } finally {
                  setAdmitting((prev) => {
                    const next = { ...prev };
                    delete next[queue.meetingId];
                    return next;
                  });
                }
              }}
              disabled={isAdmitting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-[#6a1fbf] text-xs font-semibold hover:bg-white/90 disabled:opacity-60"
            >
              {isAdmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {isAdmitting ? 'Admitting…' : 'Admit all'}
            </button>
            <a
              href={`/${queue.meetingId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium border border-white/20"
              title="Open the other meeting in a new tab"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open
            </a>
            <button
              onClick={() => onDismiss?.(queue.meetingId)}
              className="p-1 rounded hover:bg-white/15 text-white/70 hover:text-white"
              title="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
